import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Auth } from '@angular/fire/auth';
import { UserPortalService } from '../services/user-portal.service';

export interface GalleryItem {
  id: string;
  sessionId: string;
  type: 'gif' | 'print';
  url: string;
  savedAt: any;
  cutStyle?: string;
}

@Component({
  selector: 'app-gallery',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './gallery.component.html',
  styleUrl: './gallery.component.scss'
})
export class GalleryComponent implements OnInit {
  private auth = inject(Auth);
  private userPortalService = inject(UserPortalService);

  photos: any[] = [];
  galleryItems: GalleryItem[] = [];
  isLoading = true;
  selectedMedia: string | null = null;
  photoOrientations: Record<string, string> = {};

  currentPage = 1;
  pageSize = 2;

  ngOnInit() {
    const uid = this.auth.currentUser?.uid;
    if (uid) {
      this.userPortalService.getGallery(uid).subscribe({
        next: (data) => {
          this.photos = data;
          this.buildGalleryItems();
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error fetching gallery:', err);
          this.isLoading = false;
        }
      });
    } else {
      this.isLoading = false;
    }
  }

  buildGalleryItems() {
    const items: GalleryItem[] = [];
    for (const photo of this.photos) {
      if (photo.gifUrl) {
        items.push({
          id: `${photo.sessionId}_gif`,
          sessionId: photo.sessionId,
          type: 'gif',
          url: photo.gifUrl,
          savedAt: photo.savedAt,
          cutStyle: photo.cutStyle
        });
      }
      if (photo.printUrl) {
        items.push({
          id: `${photo.sessionId}_print`,
          sessionId: photo.sessionId,
          type: 'print',
          url: photo.printUrl,
          savedAt: photo.savedAt,
          cutStyle: photo.cutStyle
        });
      }
    }
    this.galleryItems = items;
    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = this.totalPages;
    }
  }

  get totalPages(): number {
    return Math.ceil(this.galleryItems.length / this.pageSize) || 1;
  }

  get paginatedItems(): GalleryItem[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.galleryItems.slice(start, start + this.pageSize);
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  onImageLoad(event: Event, itemId: string, cutStyle?: string) {
    const img = event.target as HTMLImageElement;
    if (!img.naturalWidth || !img.naturalHeight) return;

    const ratio = img.naturalWidth / img.naturalHeight;
    if (ratio > 1.1) {
      // 6x4 Landscape
      this.photoOrientations[itemId] = '6x4';
    } else {
      // 4x6 Portrait / 2x4 / 2x6 Vertical frame overlay
      this.photoOrientations[itemId] = '4x6';
    }
  }

  openModal(mediaUrl: string) {
    this.selectedMedia = mediaUrl;
  }

  closeModal() {
    this.selectedMedia = null;
  }
}
